import athenaConfig from '../config';
import { getTransformedError } from './i18n-utils';
import { userPasswordReset, shouldPromptForSymantecVIP as shouldPromptForSymantecVIPLambda } from './password-reset-api';
import { getAnetUsername } from './user-utils';
import { getStateHandle, makeRequest } from './widget-facade';

const {
  athenaOneSymantecPromptUrl,
  athenaOneChangeExpiredPasswordUrl,
  getSecurityQuestionsTokenUrl,
} = athenaConfig;

interface Username {
  username: string;
}

export interface ShouldPromptForSymantecPromptArgs extends Username {
  stateHandle: string;
}

export async function shouldPromptForSymantecVIP(data: ShouldPromptForSymantecPromptArgs) {

  try {
    if (athenaConfig.featureFlags.enableExpiredPasswordResetViaLambda) {
      const response = await shouldPromptForSymantecVIPLambda(data);
      return response.shouldPrompt;
    }
    else {
      const response = await makeAnetRequest({
        url: athenaOneSymantecPromptUrl,
        data,
      });
      if (response.prompt_symantec_vip_field) {
        return response.prompt_symantec_vip_field === 'true';
      }
      else {
        throw response;
      }
    }
  }
  catch (error: any) {
    error.checkFailed = true;
    throw error;
  }
}

/**
 * Makes http request to the given url along with the given data as urlencoded
 * form data.
 * Tries to parse if the content is json
 * or text(return text if json parse fails)
 */
export async function makeAnetRequest({ url, data }: { url: URL | string; data: any }) {
  let response;
  try {
    response = await makeRequest({
      url,
      method: 'POST',
      args: data,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }
  catch (err: any) {
    response = err;
    response.requestError = true;
  }

  const responseContentType = response.headers?.['content-type'] ?? response.xhr?.headers?.['content-type'];
  let finalResponse;
  if (responseContentType?.indexOf('application/json') > -1) {
    finalResponse = response;
  }
  else {
    const { responseText } = response;
    if (responseText) {
      try {
        finalResponse = JSON.parse(responseText);
      }
      catch (e) {
        finalResponse = responseText;
      }
    }
    else {
      finalResponse = response;
    }
  }
  finalResponse.xhr = response.xhr;
  // If errorSummary is present, there is a failure in validating the symantec flow.
  if (finalResponse.errorSummary || finalResponse.xhr?.responseJSON?.errorSummary) {
    const mayBeErrorKey = finalResponse.errorSummary;
    finalResponse.errorSummary = getTransformedError({ response: finalResponse });
    if (finalResponse.xhr?.responseJSON?.errorSummary) {
      finalResponse.xhr.responseJSON.errorSummary = finalResponse.errorSummary;
    }
    finalResponse.errorKey = mayBeErrorKey;
    throw finalResponse;
  }
  // Needs the error response itself, not the outer error object set by the above function
  if (finalResponse.xhr) {
    return finalResponse.xhr.responseJSON;
  }
  if (finalResponse.requestError) {
    throw finalResponse;
  }
  return finalResponse;
}

export interface passwordResetData {
  newPassword: string;
  stateHandle: string;
  username: string;
  clientId?: string;
  symantecSecurityCode?: string;
}

export async function passwordReset(data: passwordResetData) {
  if (athenaConfig.featureFlags.enableExpiredPasswordResetViaLambda) {
    return await userPasswordReset(data);
  }
  else {
    return makeAnetRequest({
      url: athenaOneChangeExpiredPasswordUrl,
      data: data,
    });
  }
}

function getSecurityQuestionsPayload() : any {
  return {
    username: getAnetUsername(),
    stateHandle: getStateHandle(),
  };
}

function validateGetSecurityQuestionsPayload(data: any) {
  if (!data?.username) {
    return false;
  }
  if (!data.stateHandle) {
    return false;
  }
  return true;
}

/**
 * Function makes POST call to /1/1/login/getsqtoken.esp endpoint to generate a
 * security questions JWT if the user has not setup answers to their security
 * questions.
 * @returns parsed JSON response.
 */
export async function getSecurityQuestionsPromptToken() {
  const data = getSecurityQuestionsPayload();
  if (validateGetSecurityQuestionsPayload(data) === true) {
    return await makeAnetRequest({
      url: getSecurityQuestionsTokenUrl,
      data,
    });
  }
}
